import { CABINET_PERMISSIONS, TenantRoleCode } from './cabinet-auth.js';
/**
 * ВЛАДЕЛЕЦ — весь каталог без исключений.
 *
 * Единственная роль, полнота которой выведена прямо: «Владелец (owner) | Все филиалы,
 * все мастера, все данные записи, деньги | Всё» (02-журнал-записи.md). Плюс §3.4
 * «Что видит только владелец»: тариф и счета, редактор ролей, приглашение и увольнение,
 * выгрузка базы, полный журнал действий.
 *
 * Список собирается ИЗ КАТАЛОГА, а не переписывается руками: новое право, добавленное
 * в каталог, обязано появиться у владельца автоматически — иначе владелец однажды
 * окажется единственным, кто не может открыть новый раздел.
 */
const OWNER_PERMISSIONS = CABINET_PERMISSIONS;
/**
 * УПРАВЛЯЮЩИЙ — заместитель владельца в пределах своих филиалов.
 *
 * Журнал целиком: «Филиалы из своего branchScope | Всё в пределах своих филиалов».
 * Не выдано семь вещей, закреплённых документами лично за собственником: выгрузка базы
 * (§3.5 «по умолчанию только у владельца»), тариф и счета, редактор ролей, ключи
 * публичного API (правовой контур: поручение на обработку ПД подписывает собственник),
 * вебхуки, журнал действий сотрудников и шаблоны сообщений — в строке «Кто открывает»
 * у шаблонов управляющий не назван вовсе, а догадываться документы запрещают.
 */
const MANAGER_PERMISSIONS = [
    'schedule.view',
    'schedule.export',
    'appointments.create',
    'appointments.reschedule',
    'appointments.cancel',
    'appointments.status.set',
    // «Изменять прошедшие записи может только управляющий» (01-основа.md:206).
    'appointments.past.edit',
    'busy.manage',
    'holds.manage',
    'discount.grant',
    'discount.grant.above_limit',
    'clients.list.view',
    'clients.card.view',
    'clients.phone.view',
    'clients.create',
    'clients.edit',
    'clients.notes.view',
    'clients.notes.edit',
    'clients.blacklist.manage',
    'clients.merge',
    // «Владелец или управляющий — исполнить требование человека удалить его данные».
    'clients.erase',
    // «Управляющий и владелец — убедиться, что по базе есть основания для рассылок».
    'clients.consents.manage',
    'catalog.view',
    'catalog.services.manage',
    'catalog.resources.manage',
    'catalog.prices.manage',
    'staff.view',
    'staff.manage',
    'staff.schedule.manage',
    'staff.invite',
    'branches.view',
    'branches.manage',
    'booking_rules.manage',
    'finance.view',
    'reports.view',
    'channels.manage',
    // Матрица уведомлений: экран 4-Б5 адресован владельцу и управляющему, потому что
    // включение SMS — это расход салона, а не настройка стойки.
    'notifications.rules.manage',
    'settings.view',
];
/**
 * АДМИНИСТРАТОР — человек за стойкой.
 *
 * Роль описана одной фразой в форме приглашения: «ведёт запись, видит клиентов
 * и телефоны, НЕ ВИДИТ ДЕНЬГИ И НАСТРОЙКИ». Из неё выведены и выдачи, и отказы.
 *
 * `settings.view` всё-таки выдан, и это не противоречие: под этим правом лежат
 * не «настройки салона», а чтение справочных экранов, адресованных именно
 * администратору, — состояние каналов и правила записи, по которым он объясняет
 * клиенту, почему запись не проходит. Денежных прав (`finance.view`, `billing.manage`,
 * `reports.view`) у него нет, и это ровно то, что обещает подпись.
 *
 * `appointments.past.edit` НЕ выдан намеренно: правка прошлого меняет историю
 * и отчёты, и документы закрепляют её за управляющим. Обычную правку сегодняшней
 * записи это не задевает — она идёт по `appointments.create`.
 */
const ADMIN_PERMISSIONS = [
    'schedule.view',
    'schedule.export',
    'appointments.create',
    'appointments.reschedule',
    'appointments.cancel',
    'appointments.status.set',
    'busy.manage',
    'holds.manage',
    'discount.grant',
    'clients.list.view',
    'clients.card.view',
    'clients.phone.view',
    'clients.create',
    'clients.edit',
    'clients.notes.view',
    'clients.notes.edit',
    'clients.blacklist.manage',
    'clients.merge',
    'clients.consents.manage',
    'catalog.view',
    'staff.view',
    'staff.schedule.manage',
    'branches.view',
    'settings.view',
    // Повторная отправка сообщения вручную закрыта этим правом: человеку, которому
    // клиент говорит «мне ничего не пришло», отвечать нечем без него.
    'channels.manage',
];
/**
 * МАСТЕР — граница задана СПИСКОМ ЗАПРЕТОВ, и это не паранойя, а бизнес-модель:
 * «уходящий мастер, выгрузивший базу глазами через скриншоты, — типовая история
 * отрасли» (05-мобильная-версия-и-роли.md §5.2).
 *
 * Мастер НЕ видит: выручку салона, полную базу клиентов (`clients.list.view`),
 * поиск по части номера, телефоны (это переключатель владельца, а не право роли),
 * экспорт базы, чужие записи в подробностях.
 *
 * Что выдано: свой день и свои визиты (`restrictToOwnRecords` сужает выборку
 * на сервере), создание и перенос СВОИХ записей (решение владельца 44), карточка
 * того клиента, который к нему записан, каталог услуг — без него форма записи
 * не покажет ни одной строки, — и собственный график смен.
 */
const MASTER_PERMISSIONS = [
    'schedule.view',
    'appointments.create',
    'appointments.reschedule',
    'appointments.status.set',
    'busy.manage',
    // Карточка клиента сужена на сервере до «есть в его записях»; списка базы нет.
    'clients.card.view',
    'clients.notes.view',
    'clients.notes.edit',
    // Каталог — источник услуг для формы записи. Без него мастер не запишет никого.
    'catalog.view',
    // Свой график смен лежит за этим правом. Без него мастер не видит собственных смен.
    'staff.view',
];
/**
 * БУХГАЛТЕР — приходит раз в месяц за счётом и актом.
 *
 * `billing.manage` НЕ выдан, и это решение, а не пропуск. Сегодня за этим правом лежат
 * не счета (модуля биллинга в API нет вовсе), а ПОТОЛКИ РАСХОДА на сообщения
 * и создание рассылок. Выдать его бухгалтеру значит отдать ему право поднимать
 * суточный предел трат салона и запускать рассылки — чего документы ему не дают.
 * Когда домен биллинга появится, право добавит тот, кто его напишет, и это будет
 * отдельным решением, а не следствием сегодняшнего совпадения.
 */
const ACCOUNTANT_PERMISSIONS = [
    'finance.view',
    'reports.view',
    'branches.view',
    'settings.view',
];
/**
 * МАРКЕТОЛОГ — рассылки и сегменты.
 *
 * НАБОР НЕПОЛНЫЙ, И ЭТО ЧЕСТНО НАЗВАНО ЗДЕСЬ, А НЕ СПРЯТАНО. Создание рассылки
 * и смета закрыты правом `billing.manage` (`notifications.controller.ts`), а права
 * `campaigns.manage` в каталоге НЕТ ВОВСЕ. То есть сегодня маркетолог не может
 * запустить рассылку, не получив заодно власть над деньгами салона.
 *
 * Выбор сделан в пользу отказа: выдать `billing.manage` значит отдать маркетологу
 * потолки трат и тариф. Правильное решение — завести `campaigns.manage` в каталоге
 * и перевесить маршруты рассылок на него; до тех пор роль читает отчёты и шаблоны,
 * а запускает рассылку владелец или управляющий.
 */
const MARKETER_PERMISSIONS = [
    'reports.view',
    'templates.manage',
    'clients.consents.manage',
];
/**
 * НАБЛЮДАТЕЛЬ — «сетка без персональных данных клиентов».
 *
 * ОБЕЩАНИЕ ТЗ СЕГОДНЯ НЕВЫРАЗИМО НАБОРОМ ПРАВ, и это сказано прямо: `schedule.view`
 * открывает журнал вместе с именами клиентов — отдельного права «сетка без ПД»
 * в каталоге нет. Поэтому наблюдателю выдан минимум, который не отдаёт чужие
 * персональные данные: список филиалов и отчёты.
 *
 * Пока в каталоге не появится право на обезличенный журнал, роль остаётся урезанной
 * СОЗНАТЕЛЬНО. Выдать `schedule.view` «чтобы хоть что-то работало» значило бы отдать
 * наблюдателю имена клиентов ровно в том месте, где ТЗ обещает их не показывать.
 */
const VIEWER_PERMISSIONS = ['branches.view', 'reports.view'];
export const SYSTEM_ROLES = [
    {
        code: 'owner',
        name: 'Владелец',
        restrictToOwnRecords: false,
        permissions: OWNER_PERMISSIONS,
        /*
         * ТРЕБОВАНИЕ ВТОРОГО ФАКТОРА У ЭТОЙ РОЛИ НЕ ВКЛЮЧЕНО, И ЭТО РЕШЕНИЕ ВЛАДЕЛЬЦА.
         *
         * Заведение фактора появилось (П1-24): человек, которому роль требует фактор, при
         * входе получает не отказ, а экран заведения — QR, код, резервные коды, и сразу
         * внутрь. То есть запереть владельца снаружи включение больше НЕ может.
         *
         * Но оно меняет вход каждому существующему владельцу салона: с ближайшего входа он
         * обязан завести фактор, а владелец без смартфона не войдёт, пока не найдёт
         * устройство. Такое решение принимает владелец продукта — вопрос 11 в
         * docs/design/ВОПРОСЫ-ДИЗАЙНЕРУ.md. До ответа признак выключен, и подпись роли
         * о втором факторе молчит: обещать то, чего нет, нельзя ни в какую сторону.
         */
        summary: 'Всё: салон, деньги, сотрудники, доступы.',
    },
    {
        code: 'manager',
        name: 'Управляющий',
        restrictToOwnRecords: false,
        permissions: MANAGER_PERMISSIONS,
        summary: 'Ведёт свои филиалы целиком: люди, смены, каталог, цены, выручка.',
    },
    {
        code: 'admin',
        name: 'Администратор',
        restrictToOwnRecords: false,
        permissions: ADMIN_PERMISSIONS,
        summary: 'Ведёт запись, видит клиентов и телефоны, не видит деньги и настройки салона.',
    },
    {
        code: 'master',
        name: 'Мастер',
        restrictToOwnRecords: true,
        permissions: MASTER_PERMISSIONS,
        summary: 'Свой день и свои клиенты. Базу салона и чужие записи не видит.',
    },
    {
        code: 'accountant',
        name: 'Бухгалтер',
        restrictToOwnRecords: false,
        permissions: ACCOUNTANT_PERMISSIONS,
        summary: 'Выручка и отчёты. Счета и акты появятся вместе с разделом биллинга.',
    },
    {
        code: 'marketer',
        name: 'Маркетолог',
        restrictToOwnRecords: false,
        permissions: MARKETER_PERMISSIONS,
        summary: 'Отчёты, шаблоны и согласия. Запуск рассылки пока за владельцем.',
    },
    {
        code: 'viewer',
        name: 'Наблюдатель',
        restrictToOwnRecords: false,
        permissions: VIEWER_PERMISSIONS,
        summary: 'Только чтение сводных данных, без персональных данных клиентов.',
    },
];
/** Роль по коду. Возвращает `undefined` для кода, которого нет среди системных. */
export function systemRole(code) {
    return SYSTEM_ROLES.find((role) => role.code === code);
}
// ───────────────────────── редактор ролей (4-Б11) ─────────────────────────
/**
 * ПРАВА СЛОВАМИ. Редактор ролей обязан показывать права так, как их понимает владелец
 * салона, а не машинными кодами (ТЗ 04, Б11). Запись — на каждое право каталога: тест
 * (cabinet-roles.test.ts) не даст появиться праву без подписи и без места в редакторе.
 */
export const PERMISSION_LABELS = {
    'schedule.view': 'видеть журнал записи',
    'schedule.export': 'печатать и выгружать расписание',
    'appointments.create': 'записывать клиентов',
    'appointments.reschedule': 'переносить записи',
    'appointments.cancel': 'отменять записи',
    'appointments.status.set': 'ставить статус записи: подтверждена, пришёл, завершена',
    'appointments.past.edit': 'править прошедшие записи',
    'busy.manage': 'ставить перерывы и блокировать время',
    'holds.manage': 'снимать удержания времени',
    'discount.grant': 'давать скидку в пределах лимита',
    'discount.grant.above_limit': 'давать скидку выше лимита',
    'clients.list.view': 'видеть всю базу клиентов',
    'clients.card.view': 'открывать карточку клиента',
    'clients.phone.view': 'видеть телефоны клиентов',
    'clients.create': 'заводить клиентов',
    'clients.edit': 'править карточки клиентов',
    'clients.notes.view': 'читать закрытые заметки о клиентах',
    'clients.notes.edit': 'писать заметки о клиентах',
    'clients.blacklist.manage': 'ограничивать онлайн-запись клиенту',
    'clients.merge': 'объединять дубли карточек',
    'clients.export': 'выгружать базу клиентов',
    'clients.erase': 'удалять данные клиента по его требованию',
    'clients.consents.manage': 'отмечать и отзывать согласия клиентов',
    'catalog.view': 'видеть услуги и цены',
    'catalog.services.manage': 'править услуги',
    'catalog.resources.manage': 'править кабинеты и оборудование',
    'catalog.prices.manage': 'править цены и правила цен',
    'staff.view': 'видеть сотрудников',
    'staff.manage': 'заводить сотрудников, менять и закрывать им доступ',
    'staff.schedule.manage': 'вести графики смен и отсутствия',
    'staff.invite': 'приглашать в кабинет',
    'roles.manage': 'править роли и права',
    'branches.view': 'видеть филиалы',
    'branches.manage': 'править филиалы и часы работы',
    'booking_rules.manage': 'править правила онлайн-записи',
    'finance.view': 'видеть финансы',
    'billing.manage': 'управлять тарифом и оплатой',
    'reports.view': 'видеть отчёты',
    'channels.manage': 'вести каналы связи и переотправлять сообщения',
    'notifications.rules.manage': 'настраивать уведомления клиентам (платные каналы)',
    'templates.manage': 'править шаблоны сообщений',
    'api_keys.manage': 'выпускать ключи доступа к данным',
    'webhooks.manage': 'настраивать уведомления о событиях для интеграций',
    'settings.view': 'видеть настройки салона',
    'audit.view': 'видеть журнал действий',
};
/**
 * ПРО ДЕНЬГИ И ДАННЫЕ КЛИЕНТОВ — ОТДЕЛЬНО И ЗАМЕТНО (ТЗ 04, Б11; макет П2, 4-Б11).
 * Ровно семь, ровно из ТЗ. По умолчанию у мастера нет ни одного.
 */
export const DANGER_PERMISSIONS = [
    { permission: 'clients.list.view', title: 'Видеть всю базу клиентов', note: 'список клиентов салона целиком' },
    { permission: 'clients.phone.view', title: 'Видеть телефоны клиентов', note: 'администратору нужны сразу, во время звонка' },
    { permission: 'clients.export', title: 'Выгружать базу', note: 'по умолчанию только владелец' },
    { permission: 'finance.view', title: 'Видеть финансы', note: 'выручка и деньги салона' },
    { permission: 'billing.manage', title: 'Управлять тарифом и оплатой', note: 'деньги салона у платформы' },
    { permission: 'appointments.past.edit', title: 'Править прошедшие записи', note: 'исправление ошибочной неявки' },
    { permission: 'discount.grant.above_limit', title: 'Давать скидку выше лимита', note: 'лимит скидки задаёт владелец' },
];
/**
 * РАЗДЕЛЫ — галочка на группу прав (макет 4-Б11: «Журнал: смотреть и вести»…). Галочка
 * ставит или снимает всю группу; часть группы — состояние «частично», и редактор
 * раскрывает её до отдельных прав. Каждое право каталога — ровно в одном месте: в группе
 * или среди семи опасных (проверяет тест).
 */
export const PERMISSION_GROUPS = [
    {
        id: 'journal',
        title: 'Журнал: смотреть и вести',
        permissions: [
            'schedule.view',
            'schedule.export',
            'appointments.create',
            'appointments.reschedule',
            'appointments.cancel',
            'appointments.status.set',
            'busy.manage',
            'holds.manage',
            'staff.schedule.manage',
            'discount.grant',
        ],
    },
    {
        id: 'clients',
        title: 'Клиенты: карточки и заметки',
        permissions: [
            'clients.card.view',
            'clients.create',
            'clients.edit',
            'clients.notes.view',
            'clients.notes.edit',
            'clients.blacklist.manage',
            'clients.merge',
            'clients.consents.manage',
            'clients.erase',
        ],
    },
    { id: 'catalog-view', title: 'Каталог: смотреть', permissions: ['catalog.view'] },
    {
        id: 'catalog-edit',
        title: 'Каталог: править',
        permissions: ['catalog.services.manage', 'catalog.resources.manage', 'catalog.prices.manage'],
    },
    { id: 'staff-view', title: 'Сотрудники: смотреть', permissions: ['staff.view'] },
    {
        id: 'staff-access',
        title: 'Сотрудники: приглашать, отзывать доступ',
        permissions: ['staff.invite', 'staff.manage', 'roles.manage'],
    },
    {
        id: 'branches',
        title: 'Филиалы и часы работы',
        permissions: ['branches.view', 'branches.manage', 'booking_rules.manage', 'settings.view'],
    },
    {
        id: 'channels',
        title: 'Каналы и шаблоны',
        permissions: ['channels.manage', 'templates.manage', 'notifications.rules.manage'],
    },
    { id: 'reports', title: 'Отчёты и журнал действий', permissions: ['reports.view', 'audit.view'] },
    { id: 'developers', title: 'Разработчикам', permissions: ['api_keys.manage', 'webhooks.manage'] },
];
//# sourceMappingURL=cabinet-roles.js.map